import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../../../utils/cn';
import { Skeleton } from '../../../../components/ui/Skeleton/Skeleton';
import styles from './KpiCard.module.css';

export interface KpiCardProps {
  label: string;
  /** Card value. `null` = render the placeholder dash + hint (no endpoint). */
  value: number | null;
  /** Optional Lucide icon. */
  icon?: ComponentType<LucideProps>;
  /** Where the card navigates on click. */
  to?: string;
  /** Percent change vs the previous period; null skips the row. */
  deltaPercent?: number | null;
  /** Loading shimmer state. */
  loading?: boolean;
  /** Optional hint shown beneath the placeholder dash. */
  placeholderHint?: string;
  /** Aria label override (defaults to "{label}: {value}"). */
  ariaLabel?: string;
}

function DeltaRow({ percent }: { percent: number }): JSX.Element {
  if (percent > 0) {
    return (
      <span className={cn(styles.delta, styles.deltaUp)}>
        <ArrowUp size={14} aria-hidden />
        {percent}%<span className={styles.deltaSuffix}> vs yesterday</span>
      </span>
    );
  }
  if (percent < 0) {
    return (
      <span className={cn(styles.delta, styles.deltaDown)}>
        <ArrowDown size={14} aria-hidden />
        {Math.abs(percent)}%<span className={styles.deltaSuffix}> vs yesterday</span>
      </span>
    );
  }
  return (
    <span className={cn(styles.delta, styles.deltaFlat)}>
      <ArrowRight size={14} aria-hidden />
      0%<span className={styles.deltaSuffix}> vs yesterday</span>
    </span>
  );
}

export function KpiCard({
  label,
  value,
  icon: Icon,
  to,
  deltaPercent = null,
  loading = false,
  placeholderHint,
  ariaLabel,
}: KpiCardProps): JSX.Element {
  const isPlaceholder = value === null;
  const computedAriaLabel = ariaLabel ?? `${label}: ${isPlaceholder ? 'not available' : value}`;

  const body = (
    <>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        {Icon ? (
          <span className={styles.iconWrap} aria-hidden>
            <Icon size={18} />
          </span>
        ) : null}
      </div>
      {loading ? (
        <Skeleton variant="text" width="60%" height={32} />
      ) : isPlaceholder ? (
        <>
          <div className={styles.placeholderValue} aria-label="not available">
            —
          </div>
          {placeholderHint ? <div className={styles.placeholderHint}>{placeholderHint}</div> : null}
        </>
      ) : (
        <>
          <div className={styles.value}>{value.toLocaleString()}</div>
          {deltaPercent !== null ? <DeltaRow percent={deltaPercent} /> : null}
        </>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={styles.card} aria-label={computedAriaLabel}>
        {body}
      </Link>
    );
  }
  return (
    <section className={styles.card} aria-label={computedAriaLabel}>
      {body}
    </section>
  );
}
